/**
 * The lines a pending skill row states, as a pure fold over what the reads
 * return.
 *
 * Separately exported for the same reason foldLearning.ts and foldAccess.ts
 * are: inside a component's render these rules are reachable only through a
 * browser E2E, which is not in the per-PR CI set, so a rename that emptied a
 * line would ship silently. That is not hypothetical — it is exactly what this
 * row's fields did on the way to `occurred`/`period`/`due_date`.
 *
 * Nothing here reads a clock. A `due_date` is a civil date the trainee typed
 * and a qualification's start is a day the institution named; the row STATES
 * them and derives no status from them, so the same fold answers the same on
 * any day. `pendingReason.test.ts` pins two very different system clocks and
 * asserts the answer does not move.
 */

import type { SkillEvent, Target } from '@frontend/api/endpoints/skills'
import { instantDay } from '@frontend/utils/dates'

interface PendingReasonLines {
  /**
   * The target as the read returned it, not a re-shaping of it. The row states
   * `due_date` and `basis`, which the Target already carries under those names,
   * and a second spelling here is a second place a rename has to reach. Null
   * when no target is set on the skill.
   */
  target: Target | null
  /** One line per qualification behind the skill, in the order read. */
  qualifications: { id: string; startDay: string }[]
}

/**
 * Why this skill is on her list.
 *
 * `records` is the skill's timeline. In `pending` no qualification has started
 * yet — one that had would put the skill in `held` — so every qualification the
 * timeline returns is a future one, and the day to show is its `period.start`.
 */
export function foldPendingReason(
  target: Target | null,
  records: SkillEvent[],
): PendingReasonLines {
  return {
    target,
    qualifications: records
      .filter((record) => record.kind === 'qualification')
      .map((record) => ({ id: record.id, startDay: instantDay(record.period.start) })),
  }
}
